/** (Kevin 2025-07-23)
 * Host component for link preview popovers that renders them outside of sandboxed iframes
 * This component listens for messages from iframe and renders popovers in the parent context
 */

import React, { useEffect, useState } from "react";

import Button from "@replo/design-system/components/button/Button";
import { ExternalLink, Globe } from "lucide-react";

interface PopoverMessage {
  type: "SHOW_LINK_POPOVER" | "HIDE_LINK_POPOVER";
  url?: string;
  position?: {
    top: number;
    left: number;
  };
}

interface PopoverState {
  isVisible: boolean;
  url: string;
  position: { top: number; left: number };
}

interface LinkPreviewPopoverProps {
  url: string;
  position: { top: number; left: number };
  onOpen: () => void;
}

function cleanUrl(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}

function LinkPreviewPopover({
  url,
  position,
  onOpen,
}: LinkPreviewPopoverProps) {
  const cleanedUrl = cleanUrl(url);

  return (
    <div
      className="fixed bg-white border border-border rounded-lg shadow-lg px-3 py-2 flex items-center gap-3 max-w-80"
      style={{
        top: position.top,
        left: position.left,
      }}
      data-link-popover="true"
    >
      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-muted">
        <Globe size={20} />
      </div>
      <div className="flex-1 min-w-0 text-default truncate">{cleanedUrl}</div>
      <Button
        variant="secondary"
        size="sm"
        onClick={onOpen}
        layoutClassName="h-8"
        endEnhancer={<ExternalLink size={12} />}
      >
        Open
      </Button>
    </div>
  );
}

export function LinkPreviewPopoverHost() {
  const [popover, setPopover] = useState<PopoverState>({
    isVisible: false,
    url: "",
    position: { top: 0, left: 0 },
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent<PopoverMessage>) => {
      if (
        event.data.type === "SHOW_LINK_POPOVER" &&
        event.data.url &&
        event.data.position
      ) {
        const iframe = Array.from(document.querySelectorAll("iframe")).find(
          (frame) => frame.contentWindow === event.source,
        );

        if (iframe) {
          const iframeRect = iframe.getBoundingClientRect();
          const absoluteTop = iframeRect.top + event.data.position.top;
          const absoluteLeft = iframeRect.left + event.data.position.left;

          setPopover({
            isVisible: true,
            url: event.data.url,
            position: {
              top: absoluteTop,
              left: absoluteLeft,
            },
          });
        }
      } else if (event.data.type === "HIDE_LINK_POPOVER") {
        setPopover((prev) => ({ ...prev, isVisible: false }));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!popover.isVisible) {
      return;
    }

    const handleClick = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const popoverElement = target.closest("[data-link-popover]");
      if (!popoverElement) {
        setPopover((prev) => ({ ...prev, isVisible: false }));
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [popover.isVisible]);

  const handleOpen = () => {
    window.open(popover.url, "_blank");
    setPopover((prev) => ({ ...prev, isVisible: false }));
  };

  if (!popover.isVisible) {
    return null;
  }

  return (
    <LinkPreviewPopover
      url={popover.url}
      position={popover.position}
      onOpen={handleOpen}
    />
  );
}
